<template>
	<div
		class="co-browsing-wrapper"
		ref="coBrowsingWrapper"
	>
		<div
			v-if="isCoBrowsingEnabled"
			style="height: 0px"
		>
			<CoBrowsingToolbar
				:storeCode="props.fimenu.storeCode"
				:dealNumber="props.fimenu.dealNumber"
				:dealId="props.fimenu.id"
				:viewingRole="viewingRole"
				:elementToWatch="domElementToWatch"
				:elementFacade="editorFacadeRef"
				:facadeScale="editorFacadeScale"
				:environmentName="settings.environmentName"
				:isBusyIniting="props.isBusyLoadingDeal"
				@followCustomerScreen="followCustomerScreenToggle"
			/>

			<div
				v-show="followEditor"
				ref="editorFacade"
				class="editor-facade-container"
				:style="editorFacadeStyle"
			>
				<IsBusySectionComponent v-if="!editorHTMLReceived" />
				<div class="facade-mask" />

				<div
					v-show="followCustomerScreen"
					ref="customerFacadeDump"
					class="facade-customer"
				/>

				<div
					v-show="!followCustomerScreen"
					ref="editorFacadeDump"
					class="facade-fimenu app-container"
				/>

				<footer v-if="!followCustomerScreen">You are currently viewing the Editor's screen</footer>
			</div>
		</div>

		<div
			v-show="!followEditor || !props.isCoBrowsingEnabled"
			class="deal-container"
		>
			<slot></slot>
		</div>
	</div>
</template>
<script lang="ts">
	export interface ICoBrowsingWrapperProps {
		/** Whether or not co-browsing should be enabled. If it's not enabled, will not attempt to connect to the deal room hub. */
		isCoBrowsingEnabled: boolean;

		/** A set of data to attach a watch to (with a max depth of 4). When an update is detected, will emit `fimenu` into the deal room hub. */
		dataToWatch?: unknown;

		fimenu: FIMenu;
		isBusyLoadingDeal: boolean;
	}
</script>
<script setup lang="ts">
	// TODO: Now that this component exists, should probably simplify some functionality from CoBrowsingToolbar and move it into here.
	import { computed, inject, nextTick, onBeforeMount, onBeforeUnmount, ref, useTemplateRef, watch, WatchHandle } from 'vue';

	import $modal from '@core/services/modal';
	import auth from '@core/services/auth';
	import { Emitter } from 'mitt';
	import settings from '@/settings';
	import util from '@core/services/util';

	import { ICB_HTMLCoordinates, ICB_RoomInfo, ICB_ViewerInfo } from '@core/classes/CoBrowsingModels';
	import { DealRoomHubPlugin } from '@core/plugins/dealRoomHub';
	import FIMenu from '@core/classes/FIMenu';

	import modalInfo from '@core/modals/modalInfo.vue';

	import CoBrowsingToolbar from '@/components/CoBrowsing/CoBrowsingToolbar.vue';
	import IsBusySectionComponent from '@/components/IsBusySectionComponent.vue';

	const $dealRoomHub = inject<DealRoomHubPlugin>('$dealRoomHub');
	const EventBus = inject<Emitter<any>>('EventBus');

	const editorFacadeRef = useTemplateRef<HTMLDivElement>('editorFacade');
	const customerFacadeDumpRef = useTemplateRef<HTMLDivElement>('customerFacadeDump');
	const editorFacadeDumpRef = useTemplateRef<HTMLDivElement>('editorFacadeDump');
	const coBrowsingWrapperRef = useTemplateRef<HTMLDivElement>('coBrowsingWrapper');

	const AUTH_PAYLOAD = auth.getTokenPayload();
	const MAX_WATCH_DEPTH = 4;

	const props = defineProps<ICoBrowsingWrapperProps>();

	// ===============
	// LIFECYCLE HOOKS
	// ===============
	// #region Lifecycle Hooks
	onBeforeMount(() => {
		setupDealRoomHub(true);
		setupCoBrowsing();
	});

	onBeforeUnmount(async () => {
		setupDealRoomHub(false);
		await alertDisconnectAndClose();
		window.removeEventListener('resize', windowResizeHandler);
		stopAutoPing();
	});
	// #endregion

	// =========================
	// COBROWSING SETUP + EVENTS
	// =========================
	// #region CoBrowsing Setup + Events
	const viewingRole = ref<'Editor' | 'Spectator' | 'Unknown'>(null);

	const isSpectator = computed((): boolean => viewingRole.value === 'Spectator');

	const followEditor = ref(false);
	const dealRoomViewerCount = ref(0);
	const dealRoomFollowerCount = ref(0);

	const setupCoBrowsing = () => {
		if (!props.isCoBrowsingEnabled) return;

		domElementToWatch.value = document.querySelector('.app-container');

		window.addEventListener('resize', windowResizeHandler);

		watch(dealUpdatedExternally, newVal => {
			EventBus.emit('dealUpdatedExternally', newVal);
			setTimeout(() => {
				dealUpdatedExternally.value = false;
			}, 100);
		});
	};

	const setupDealRoomHub = (isSubscribing: boolean) => {
		const dealRoomSubscriptions = {
			RequestForDeal: requestForDealHandler,
			ViewingRoleChanged: viewingRoleChangedHandler,
			UpdatedRoomInfo: updatedRoomInfoHandler,
			SyncFollowEditor: syncFollowEditorHandler,
			EditorHTMLUpdate: editorHTMLUpdateHandler,
			EditorScrollUpdate: editorScrollUpdateHandler,
			DealHasBeenUpdated: dealHasBeenUpdatedHandler,
		};

		if (isSubscribing) $dealRoomHub.bulkSubscribe(dealRoomSubscriptions);
		else $dealRoomHub.bulkUnsubscribe(dealRoomSubscriptions);
	};

	const updatedRoomInfoHandler = async (roomInfo: ICB_RoomInfo, viewerInfo: ICB_ViewerInfo) => {
		dealRoomViewerCount.value = roomInfo.viewerCount;
		dealRoomFollowerCount.value = roomInfo.followerCount ?? 0;

		if (viewingRole.value && viewingRole.value !== viewerInfo.role) {
			const roleName = viewerInfo.role === 'Spectator' ? 'Viewer' : viewerInfo.role;

			$modal.open(modalInfo, {
				name: 'modalInfo',
				passedData: {
					title: `Role Changed to ${viewerInfo.role}`,
					info: `Your viewing role has been changed to ${roleName}`,
					acceptText: 'OK',
				},
			});
		}

		viewingRole.value = viewerInfo.role;
		followEditor.value = viewerInfo.followEditor;
		setWatchEditorSections();
	};

	const viewingRoleChangedHandler = (role: 'Editor' | 'Spectator' | 'Unknown') => {
		// Close any modals that may have been opened while editing
		if (!isSpectator.value && role === 'Spectator') {
			$modal.closeAll();
		}

		if (viewingRole.value && viewingRole.value !== role) {
			$modal.open(modalInfo, {
				name: 'modalInfo',
				passedData: {
					title: `Role Changed to ${role}`,
					info: `Your viewing role has been changed to ${role}`,
					acceptText: 'OK',
				},
			});
		}

		viewingRole.value = role;
		if (role === 'Editor') followEditor.value = false;
		setWatchEditorSections();
	};

	const syncFollowEditorHandler = (newValue: boolean) => {
		followEditor.value = newValue;
	};

	const requestForDealHandler = async () => {
		await $dealRoomHub.updateDealForConnection(props.fimenu);
	};

	const dealHasBeenUpdatedHandler = () => {
		dealUpdatedExternally.value = true;
	};

	const alertDisconnectAndClose = async () => {
		await $dealRoomHub.alertDisconnect(true);
	};
	// #endregion

	// ===================
	// EDITOR DATA WATCHER
	// ===================
	// #region Editor Data Watcher
	const editorDataWatcher = ref<WatchHandle>(null);
	const dealUpdatedExternally = ref(false);

	const setWatchEditorSections = () => {
		if (isSpectator.value && editorDataWatcher.value) {
			editorDataWatcher.value(); // Unwatch
			editorDataWatcher.value = null;
		} else if (!isSpectator.value && !editorDataWatcher.value) {
			editorDataWatcher.value = watch(
				() => props.dataToWatch,
				() => {
					// Don't bother sending if alone in a room
					if (dealRoomViewerCount.value < 2) return;

					util.debounce(watchEditorSectionsDebounced, 150);
				},
				{ deep: MAX_WATCH_DEPTH },
			);
		}
	};

	const watchEditorSectionsDebounced = () => {
		if (!dealUpdatedExternally.value && !props.isBusyLoadingDeal) {
			$dealRoomHub.alertDealUpdated(props.fimenu);
		} else {
			dealUpdatedExternally.value = false;
		}
	};
	// #endregion

	// ==================
	// AUTO PING INTERVAL
	// ==================
	// #region Auto-Ping Interval
	const autoPingInterval = ref<NodeJS.Timeout>(null);
	const isConnected = computed((): boolean => $dealRoomHub.connected);

	watch(isConnected, newValue => {
		if (newValue) setupAutoPing();
		else stopAutoPing();
	});

	const setupAutoPing = () => {
		autoPingInterval.value = setInterval(async () => {
			/*eslint-disable-next-line no-console */
			console.log('DEAL AUTO PING - **PING**');

			await $dealRoomHub.ping();
		}, 30000);
	};

	const stopAutoPing = () => {
		// eslint-disable-next-line no-console
		console.log('DEAL AUTO PING - DISABLED');
		clearInterval(autoPingInterval.value);
	};
	// #endregion

	// ============
	// HTML SYNCING
	// ============
	// #region HTML Syncing
	const domElementToWatch = ref<HTMLElement>(null);
	const editorHTMLReceived = ref(false);

	const editorHTMLUpdateHandler = (editorHTML: string, screenXY: ICB_HTMLCoordinates, elementXY: ICB_HTMLCoordinates) => {
		if (!editorHTMLReceived.value) editorHTMLReceived.value = true;
		nextTick(() => {
			if (!editorFacadeDumpRef.value) return;

			const oldScrollPositions = Array.from(editorFacadeDumpRef.value.querySelectorAll('[scrollpercent]')).map(element => element.scrollTop);

			editorFacadeDumpRef.value.innerHTML = editorHTML;

			editorFacadeDumpRef.value.style.width = `${elementXY.x}px`;
			editorFacadeDumpRef.value.style.height = `${elementXY.y}px`;

			editorScreenDimensions.value.screenY = screenXY.y;
			editorScreenDimensions.value.screenX = screenXY.x;

			// If admin, remove cobrowsing blur censors
			if (AUTH_PAYLOAD.isAdmin) {
				const cobrowsingCensors = editorFacadeDumpRef.value.querySelectorAll('.cobrowsing-censor');
				cobrowsingCensors.forEach(element => {
					(element as HTMLElement).style.filter = 'unset';
				});
			}

			// Set values of textarea elements so that the value gets rendered correctly
			const textareaElems = editorFacadeDumpRef.value.querySelectorAll('textarea');
			textareaElems?.forEach(elem => (elem.value = elem.getAttribute('value')));

			// Set initial scroll
			const scrolledElements = Array.from(editorFacadeDumpRef.value.querySelectorAll('[scrollpercent]'));
			for (let i = 0; i < scrolledElements.length; i++) {
				const currentElem = scrolledElements[i];

				currentElem.scrollTop = oldScrollPositions[i] ?? 0;
				nextTick(() => {
					const scrollAmt = Number(currentElem.getAttribute('scrollpercent')) * (currentElem.scrollHeight - currentElem.clientHeight);
					currentElem.scrollTo({ top: scrollAmt });
				});
			}

			// Set live customer view screen, if available
			customerFacadeDumpRef.value.innerHTML = editorFacadeDumpRef.value.querySelector('.live-screen-container')?.outerHTML;
			if (!followCustomerScreen.value) {
				editorScreenDimensions.value.customerX = screenXY.x;
				editorScreenDimensions.value.customerY = screenXY.y;
				customerFacadeDumpRef.value.style.width = `${screenXY.x}px`;
				customerFacadeDumpRef.value.style.height = `${screenXY.y}px`;
				return;
			}

			const customerScreen = customerFacadeDumpRef.value.querySelector('.live-screen-container');
			nextTick(() => {
				const liveScreen = customerScreen.querySelector('.live-screen .customer-container, .live-screen') as HTMLElement;

				const customerWidth = liveScreen?.offsetWidth ?? 0;
				const customerHeight = liveScreen?.offsetHeight ?? 0;

				editorScreenDimensions.value.customerX = customerWidth > 0 ? customerWidth : screenXY.x;
				editorScreenDimensions.value.customerY = customerHeight > 0 ? customerHeight : screenXY.y;

				customerFacadeDumpRef.value.style.width = `${editorScreenDimensions.value.customerX}px`;
				customerFacadeDumpRef.value.style.height = `${editorScreenDimensions.value.customerY}px`;
			});
		});
	};

	const editorScrollUpdateHandler = (targetClass: string, scrollPercent: number) => {
		if (!followEditor.value) return;

		const scrolledElement = editorFacadeRef.value.querySelector(targetClass);
		if (scrolledElement) {
			const scrollAmt = scrollPercent * (scrolledElement.scrollHeight - scrolledElement.clientHeight);
			scrolledElement.scrollTo({ top: scrollAmt, behavior: 'smooth' });
		}
	};
	// #endregion

	// ===============
	// SCREEN RESIZING
	// ===============
	// #region Screen Resizing
	const screenResizedToggle = ref(0);

	const editorScreenDimensions = ref({
		screenX: 0,
		screenY: 0,
		customerX: 0,
		customerY: 0,
	});

	const editorFacadeStyle = computed((): string => {
		if (!editorHTMLReceived.value) return `width: 90%;`;

		const dim = editorScreenDimensions.value;
		const width = followCustomerScreen.value && dim.customerX > 0 ? dim.customerX : dim.screenX;
		const height = followCustomerScreen.value && dim.customerY > 0 ? dim.customerY : dim.screenY;

		return `scale: ${followCustomerScreen.value ? customerFacadeScale.value : editorFacadeScale.value};
                width: ${width}px;
                height: ${height}px;`;
	});

	const editorFacadeScale = computed((): number => {
		// Forces value to recalculate
		if (!screenResizedToggle.value && (editorScreenDimensions.value.screenX === 0 || editorScreenDimensions.value.screenY === 0)) return 0.9;

		const ourScreenWidth = coBrowsingWrapperRef.value?.clientWidth ?? window.innerWidth;
		const ourScreenHeight = coBrowsingWrapperRef.value?.clientHeight ?? window.innerHeight;

		// Editor Facade has a 5px border, need to account for this offset
		const ratioX = (ourScreenWidth - 10) / editorScreenDimensions.value.screenX;
		const ratioY = (ourScreenHeight - 10) / editorScreenDimensions.value.screenY;

		return Math.min(ratioX, ratioY);
	});

	const customerFacadeScale = computed(() => {
		if (!screenResizedToggle.value && (editorScreenDimensions.value.customerX === 0 || editorScreenDimensions.value.customerY === 0)) return 0.9;

		const ourScreenWidth = coBrowsingWrapperRef.value?.clientWidth ?? window.innerWidth;
		const ourScreenHeight = coBrowsingWrapperRef.value?.clientHeight ?? window.innerHeight;

		// Editor Facade has a 5px border, need to account for this offset
		const ratioX = (ourScreenWidth - 10) / editorScreenDimensions.value.customerX;
		const ratioY = (ourScreenHeight - 10) / editorScreenDimensions.value.customerY;

		return Math.min(ratioX, ratioY) - 0.1;
	});

	const windowResizeHandler = () => {
		if (!props.isCoBrowsingEnabled || !$dealRoomHub.connected || dealRoomViewerCount.value < 2) return;
		util.debounce(windowResizeHandlerDebounced, 100);
	};

	const windowResizeHandlerDebounced = () => {
		screenResizedToggle.value++;
	};
	// #endregion

	// ======================
	// CUSTOMER SCREEN TOGGLE
	// ======================
	// #region Customer Screen Toggle
	const followCustomerScreen = ref(false);

	const followCustomerScreenToggle = (newValue: boolean) => {
		followCustomerScreen.value = newValue;
	};
	// #endregion
</script>
<style scoped>
	.co-browsing-wrapper {
		height: 100%;
	}

	.co-browsing-wrapper > .deal-container {
		height: 100%;
	}

	.editor-facade-container {
		width: fit-content;
		transform-origin: 0% 0%;
		border: 5px solid var(--error-color);
		filter: grayscale(0.8);
		transition: scale 0.3s ease-in-out;
		width: 100%;
	}

	.editor-facade-container * {
		animation: none;
	}

	.editor-facade-container > *:not(footer) {
		position: relative;
	}

	.editor-facade-container .facade-mask {
		position: absolute;
		left: 0;
		right: 0;
		top: 0;
		bottom: 0;
		opacity: 0;
		user-select: none;
		z-index: 2000;
	}

	.editor-facade-container .facade-customer .live-screen-container {
		height: 100%;
		width: 100%;
		margin-top: unset;
	}

	.editor-facade-container .facade-customer .live-screen-container .maximize {
		display: none;
	}

	.editor-facade-container .facade-customer .live-screen-container .live-screen {
		width: 100%;
		height: 100%;
		border: unset;
	}

	.editor-facade-container .facade-customer .live-screen > div {
		transform: scale(1) !important;
	}

	.editor-facade-container .facade-fimenu {
		z-index: 1;
	}

	.editor-facade-container .modal .modal-container {
		overflow: hidden;
		animation-name: unset !important;
		animation-duration: unset !important;
		transition: none !important;
		max-height: 90%;
	}

	.editor-facade-container footer {
		z-index: 0;
	}
</style>
